
/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import { getFirstSSN } from "./formatSSN";
import { States } from "interfaces/common";
import { getCurrentEnv } from "./getCurrentEnv";
import { formatBirthDate, formatDate } from "utils/formatDate";
import dayjs from "dayjs";
import { urls } from "urls";
import { Config } from "interfaces/config";


const STATE = getCurrentEnv

export type AgencyDecisionKey =
    | "HIT"
    | "NO_HIT"
    | "NAME_SEARCH"
    | "QC_REJECT"
    | "ERRT"
    | "NO_DECISION";

export type AgencyStatusMap = Record<AgencyDecisionKey, string>;

export const DEFAULT_AGENCY_STATUS_MAP: AgencyStatusMap = {
    HIT: "Record Found",
    NO_HIT: "No Record Found",
    NAME_SEARCH: "Name Search",
    QC_REJECT: "N/A",
    ERRT: "N/A",
    NO_DECISION: "Results Pending",
};

const ERROR_STATUSES = new Set([
    "ERRT-Error",
    "QC Reject",
    "QC Reject NoRBsub",
    "ERRT-Error NoRBsub",
    "Error",
]);

export const convertDate = (date: any, type: any) => {
    switch (type) {
        case 'dob':
            console.log('date for date of birth: ', date)
            const flippedDate = `${date.substring(4, 8)}${date.substring(0, 2)}${date.substring(2, 4)}`;
            return formatDate(flippedDate);
        case 'insertDate':
            console.log('date for insert date: ', date)
            return formatDate(date);
        case 'viewedDate':
            if (date === "N/A") {
                return "N/A"
            } else {
                return formatDate(date);
            }

        default:
            console.log('date for rapback: ', formatDate(date))
            return formatDate(date);
    }
}


export const splitDataToPages = (data: any, pageSize: number, useTableStore: any) => {
    console.log('split data to pages data: ', data)
    return new Promise((resolve) => {
        if (data) {
            console.log('paginate data: ', data.length)
            const res: Array<any> = [];
            for (let i = 0; i < data.length; i += pageSize) {
                const chunk = data.slice(i, i + pageSize);
                res.push(chunk);
            }
            resolve(res);
        }
    })
}

export const extendViewing = async (id: any, expireDate: string, numDaysExtended: number, config: Config) => {
    console.log('debug extend extendModal dates: debug extenderViewing function config: ', { id, expireDate, numDaysExtended, config })
    const STATE = process.env.REACT_APP_CONFIG_STATE!

    const { hardLimit, softLimit, extendDays } = config.transactionViewing;
    // const transactionDate = useTableStore(state => state.transactionDate)

    console.log("extendViewing id: ", id);

    // Enforce 90-day hardLimit cutoff
    const maxExpireDate = dayjs().subtract(hardLimit, "day");
    if (dayjs(expireDate).isBefore(maxExpireDate)) {
        return new Promise((resolve) => {
            resolve({ status: 400, msg: `Transaction cannot be extended beyond ${hardLimit} days from today.` });
        });
    }

    const controller = new AbortController();
    const { signal } = controller;
    console.log("extend process.env.NODE_ENV", process.env.NODE_ENV);
    const adjudicationUrl = urls.ADJUDICATION;
    const failed = false;
    return new Promise((resolve, reject) => {
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.NODE_ENV === 'production' ? adjudicationUrl : ""}/adjudication/transactions/extendViewing/${id}?expireDate=${expireDate}`,
                    {
                        method: "POST",
                        credentials: "include",
                        signal,
                    }
                );
                console.log('response', res);
                const status = res.status;
                if (status === 200) {
                    /*const response = await res.json();
                    console.log('checking the response', response)
                    const message = response.message
                    let msgSplit = message.split("for ")
                    let extendedFor = parseInt(msgSplit[1].split(" ")[0])
                    let date = dayjs().add(extendedFor, "days").format("LL")
                    console.log('message date ', date)*/
                    resolve({
                        status: res.status,
                        msg: `Transaction Extended
                    The applicant has been extended ${numDaysExtended} days. Transaction extended through ${dayjs(expireDate).format('LL')}`
                    });

                } else {
                    console.log('failed status', status);
                    resolve({ status: res.status, msg: `Transaction cannot be extended, ${hardLimit} day max limit extension has been reached,  please contact support.` });
                }

            } catch (e: any) {
                console.log("error extending viewing for id: ", id);
            }
        })()
    });
};

const createName = (nameString: string) => {
    if (nameString.includes(",")) {
        let split = nameString.split(/[,]+/)
        let last = split[0]
        let firstMiddle = split[1].split(" ")
        firstMiddle.shift()
        let first = firstMiddle[0]
        let middle = firstMiddle[1]
        return [last, first, middle]
    } else {
        return nameString
    }
}

export const transformRejectData = (data: any, index: number, field60IsObject: boolean) => {
    let row;
    let splitString = field60IsObject ? data.V1_field60 : data;
    splitString = splitString.split(/ - (.*)/);
    console.log(`Split of the rejection code${splitString[0]},${splitString[1]}`)

    row = {
        key: index,
        rejectIndex: index + 1,
        rejectCode: splitString[0],
        rejectDescription: splitString[1]
    }
    return row
}

export function resolveAgencyDecisionKey(
    result: string,
    transactionStatus: string,
): AgencyDecisionKey {
    if (ERROR_STATUSES.has(transactionStatus)) {
        return "QC_REJECT";
    }

    switch (result) {
        case "I":
        case "Y":
            return "HIT";

        case "N":
            return "NO_HIT";

        case "NS":
            return "NAME_SEARCH";

        default:
            return "NO_DECISION";
    }
}

export function mapHitNoHitResponse(
    result: string,
    transactionStatus: string,
    agencyStatusMap?: Partial<AgencyStatusMap>,
): string {
    const decisionKey = resolveAgencyDecisionKey(result, transactionStatus);

    const statusMap: AgencyStatusMap = agencyStatusMap && typeof agencyStatusMap === "object" ? { ...DEFAULT_AGENCY_STATUS_MAP, ...agencyStatusMap } : { ...DEFAULT_AGENCY_STATUS_MAP }

    return statusMap[decisionKey];
}

export const transform = (data: any, _i?: number) => {
    console.log('useQueryTransactions transform data: ', data)
    let lastName, firstName, middleName;
    if (data.name) {
        [lastName, firstName, middleName] = createName(data.name);
    }

    const parseFullName = (name: string) => {
        if (name.includes(",")) {
            let arr = name.split(",")
            let firstName, middleName
            let lastName = arr[0]
            let firstMiddle = arr[1].trim().split(" ")
            firstName = firstMiddle[0]
            middleName = firstMiddle[1]
            return `${firstName} ${lastName}`
        } else {
            return name
        }
    }

    const row = {
        key: data.id ?? '-',
        view: data.id ?? '-',
        fullName: data.name ? parseFullName(data.name) : '-',
        firstName: (data.FIRST_NAME || data.firstName || firstName) ?? '-', //Last,First M || Last, First M
        lastName: (data.LAST_NAME || data.lastName || lastName) ?? '-', //Last,First M || Last, First M
        middleName: (data.MIDDLE_NAME || data.middleName || middleName) ?? '-',
        ueid: (data.UEID || data.ueid) ?? '-',
        ssn: getFirstSSN(data.ssn),
        dob: data.dob ? formatBirthDate(data.dob, "YYYYMMDD") : '-',
        gender: data.gender ?? '-',
        race: data.race ?? '-',
        expireDate: data.expireDate,
        extendCount: data.extendCount,
        status: data.transactionStatus ? data.transactionStatus.split('"').join("") : '-', //transactionStatus
        hitNoHit: data.transactionStatus ? mapHitNoHitResponse(data.hitNoHit, data.transactionStatus, data.agencyStatusMap) : '-',
        transactionDate: data.insertDate ? formatDate(data.insertDate) : '-', //date convert to mm/dd/yyyy am/pm comes in year day month "2022-05-03T00:00:00Z" ask insertDate
        tcn: (data.TCN || data.tcn) ?? '-', //id
        transactionType: (data.TOT || data.tot) ?? '-', //transactionType
        agency: (data.AGENCY || data.agency) ?? '-', //agency
        secondaryOri: data.secondaryOri ?? '-',
        stateRFPCode: data.stateRFPCode ?? '-',
        stateRFPLiteral: data.stateRFPLiteral ?? '-', //this is GA thing. Will need additional transform logic for other states
        viewed: data.viewed,
        viewedDate: data.viewedDate ?? 'N/A',
        viewedUser: data.viewedUser,
        rfp: (data.RFP || data.rfp || data.stateRFPLiteral) ?? "-",
        rbSub: data.rbSub
    }
    console.log("asdfsdafsda", row);
    return row;
};

const parseName = (name: string) => {
    let arr = name.split(", ")
    if (arr.length === 1) {
        return name
    }
    if (arr[1].includes(" ")) {
        let firstMiddle = arr[1].split(" ")
        console.log('firstMiddle: ', firstMiddle)
        return `${firstMiddle[0]} ${firstMiddle[1]} ${arr[0]}`
    } else {
        return `${arr[1]} ${arr[0]}`
    }
}

export const transformMaintenanceData = (data: any, index: number) => {
    console.log('transformMaintenacnceData data: ', data)

    return {
        key: data.id,
        fullName: parseName(data.fullName),
        dob: data.dob != "N/A" ? formatDate(data.dob) : "N/A",
        rapbackSubId: data.rapbackSubId,
        ssn: data.ssn === null ? "123-45-6789" : data.ssn,
        lsTcn: data.lsTcn,
        ori: data.ori,
        subscriptionDate: formatDate(data.subscriptionDate),
        expirationDate: data.expirationDate != "N/A" ? formatDate(data.expirationDate) : "N/A",
        deletionDate: data.deletionDate != "N/A" ? formatDate(data.deletionDate) : "N/A",
        status: data.status
    }
}

export const transformPrenotificationsData = (data: any, index: number) => {
    console.log(`debug validations prenotifications data: ${JSON.stringify(data, null, 2)}`)
    return {
        key: data.id,
        fullName: parseName(data.fullName),
        dob: data.dob != "N/A" ? formatDate(data.dob) : "N/A",
        ssn: data.ssn === "n/a" ? "123-45-6789" : data.ssn,
        rapbackSubId: data.rapbackSubId, //is this rapback id?
        primaryOri: data.ori,
        subscriptionDate: data.subscriptionDate != "N/A" ? formatDate(data.subscriptionDate) : "N/A",
        expirationDate: data.expirationDate != "N/A" ? formatDate(data.expirationDate) : "N/A",
        receivedDate: data.needsConfirmDate != "N/A" ? formatDate(data.needsConfirmDate) : "N/A"
    }
}

export const transformValidationsData = (data: any, index: number) => {
    console.log('debug validations transformValidationsData: ', data)
    return {
        key: data.id,
        fullName: parseName(data.fullName),
        dob: data.dob != "N/A" ? formatDate(data.dob) : "N/A",
        ssn: data.ssn === null ? "no-ssn" : data.ssn,
        oris: data.ori + " " + data.sOri,
        rapbackSubId: data.rapbackSubId, //is this rapback id?
        primaryOri: data.ori,
        //secondaryOri: data.s_ori,
        subscriptionDate: data.subscriptionDate != "N/A" ? formatDate(data.subscriptionDate) : "N/A",
        expirationDate: data.expirationDate != "N/A" ? formatDate(data.expirationDate) : "N/A",
        timeRemaining: data.daysToExpire,
    }
}

export const transformBatchPrintData = (data: any, index: any) => {
    return {
        key: data.id,
        firstName: data.firstName,
        lastName: data.lastName,
        reasonFingerPrinted: data.rfp,
        status: data.transactionStatus,
        transactionDate: formatDate(data.insertDate),
        transactionNumber: data.tcn,
        ori: data.agency,
        applicantType: data.cardType
    };
}

export const checkInvalidDate = (value: any) => {
    if (value === "Invalid Date") {
        return "null"
    } else {
        return value
    }
}

export const formatDob = (val?: string): string => {
    if (!val || val === "null" || val === "") return "null";

    let parsed;

    // Handle DD/MM/YYYY format explicitly
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val)) {
        parsed = dayjs(val, 'DD/MM/YYYY');
    }
    // Handle YYYY-MM-DD format
    else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(val)) {
        parsed = dayjs(val, 'YYYY-MM-DD');
    }
    // Handle YYYYMMDD format
    else if (/^\d{8}$/.test(val)) {
        parsed = dayjs(val, 'YYYYMMDD');
    }
    // Default parsing for other formats
    else {
        parsed = dayjs(val);
    }

    if (!parsed.isValid()) {
        console.warn(`Invalid DOB date: ${val}, defaulting to null`);
        return "null";
    }

    return parsed.format("YYYYMMDD");
};

export const normalizeHitNoHit = (val?: string): string => {
    if (!val || val === "null" || val === "") return "null";

    const normalized = val.toLowerCase().trim();
    const mapping: Record<string, string> = {
        "y": "recordFound",
        "yes": "recordFound",
        "hit": "recordFound",
        "found": "recordFound",
        "record found": "recordFound",
        "recordfound": "recordFound",
        "not eligible": "recordFound",

        "n": "noRecordFound",
        "no": "noRecordFound",
        "nohit": "noRecordFound",
        "not": "noRecordFound",
        "notfound": "noRecordFound",
        "not found": "noRecordFound",
        "no record found": "noRecordFound",
        "eligible": "noRecordFound",

        "ns": "nameSearch",
        "name": "nameSearch",
        "namesearch": "nameSearch",
        "name search": "nameSearch",

        "n/a": "N/A",
        "na": "NA",

        "pending": "resultsPending",
        "resultspending": "resultsPending",
        "results pending": "resultsPending"
    };

    return mapping[normalized] || val; // fallback to original value
};

export const transformRapBackData = (data: any, i: number) => {
    console.log(`transformedData rapback inside STATE === States.GA ${STATE === States.GA} transform data: `, data)

    return {
        key: data.id, //id coould also be an index
        view: data.id,
        firstName: data.FIRST_NAME || data.firstName, //Last,First M || Last, First M
        lastName: data.LAST_NAME || data.lastName, //Last,First M || Last, First M
        middleName: (data.MIDDLE_NAME != "N/A" ? data.MIDDLE_NAME : "") || (data.middleName != "N/A" ? data.middleName : ""),
        agency: data.agency,
        ...STATE === States.GA ? { primaryOri: data.primaryOri, secondaryOri: data.secondaryOri } : { oris: data.oris },
        sid: data.sid,
        ssn: getFirstSSN(data.ssn),
        dob: convertDate(data.dob, 'dob'),
        reason: data.reason,
        isHit: data.isHit.toUpperCase(),
        rapbackDate: convertDate(data.rapbackDate, 'rapbackDate'),
        fileData: data.fileData,
        insertDate: convertDate(data.insertDate, 'insertDate'),
        viewed: data.viewed,
        viewedDate: convertDate(data.viewedDate, 'viewedDate'),
        viewedUser: data.viewedUser
    }
}

// Utility to map frontend field names to their corresponding backend equivalents
export const mapToBackendFieldName = (field: string) => (
    {
        'transactionNumber': 'tcn',
        'reasonFingerprinted': 'rfp'
    }[field] || field
)
